import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import {
  Search, User, DollarSign, Calendar, Tag, LayoutDashboard,
  Briefcase, Camera, Calculator, FileText, Settings,
} from 'lucide-react';

interface SearchResult {
  type: 'client' | 'deal' | 'navigation';
  id?: string;
  title: string;
  subtitle?: string;
  path: string;
  icon: any;
}

const NAV_ITEMS: SearchResult[] = [
  { type: 'navigation', title: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { type: 'navigation', title: 'Deals', path: '/deals', icon: DollarSign },
  { type: 'navigation', title: 'Proforma Invoices', path: '/finance/proforma', icon: FileText },
  { type: 'navigation', title: 'Reports', path: '/reports', icon: FileText },
  { type: 'navigation', title: 'Settings', path: '/settings', icon: Settings },
  { type: 'navigation', title: 'Services', path: '/services', icon: Tag },
  { type: 'navigation', title: 'Lists', path: '/lists', icon: FileText },
  
  { type: 'navigation', title: 'Team Bonus', path: '/team-bonus', icon: DollarSign },
  { type: 'navigation', title: 'Notifications', path: '/notifications', icon: FileText },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  // Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const search = useCallback(async (q: string) => {
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    const lower = q.toLowerCase();

    // Navigation results
    const navResults = NAV_ITEMS.filter(n => n.title.toLowerCase().includes(lower)).slice(0, 5);

    // DB search
    let dbResults: SearchResult[] = [];
    try {
      const [clientsRes, dealsRes] = await Promise.all([
        supabase.from('leads').select('id, name, phone')
          .or(`name.ilike.%${q}%,phone.ilike.%${q}%`)
          .limit(5),
        supabase.from('deals').select('id, deal_code, lead_name, agent_name, total_eur')
          .or(`deal_code.ilike.%${q}%,lead_name.ilike.%${q}%,agent_name.ilike.%${q}%`)
          .limit(5),
      ]);

      if (clientsRes.data) {
        dbResults.push(...clientsRes.data.map(c => ({
          type: 'client' as const, id: c.id,
          title: c.name || 'Unknown',
          subtitle: c.phone || '', path: `/client/${c.id}`, icon: User,
        })));
      }
      if (dealsRes.data) {
        dbResults.push(...dealsRes.data.map(d => ({
          type: 'deal' as const, id: d.id,
          title: `${d.deal_code} — ${d.lead_name}`,
          subtitle: `${d.agent_name} · €${d.total_eur}`,
          path: `/deals/${d.id}`, icon: DollarSign,
        })));
      }
    } catch { /* ignore search errors */ }

    setResults([...navResults, ...dbResults]);
    setSearching(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => search(query), 200);
    return () => clearTimeout(timer);
  }, [query, search]);

  const handleSelect = (result: SearchResult) => {
    navigate(result.path);
    setOpen(false);
    setQuery('');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="p-0 max-w-lg gap-0">
        <div className="flex items-center border-b px-3">
          <Search className="h-4 w-4 text-muted-foreground mr-2 shrink-0" />
          <Input
            value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search clients, deals, pages..."
            className="border-0 focus-visible:ring-0 h-12 text-sm"
            autoFocus
          />
          <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 text-[10px] font-mono text-muted-foreground">ESC</kbd>
        </div>
        <div className="max-h-80 overflow-y-auto p-1">
          {query && results.length === 0 && !searching && (
            <p className="text-sm text-muted-foreground text-center py-6">No results found</p>
          )}
          {results.map((r, i) => {
            const Icon = r.icon;
            return (
              <button key={`${r.type}-${r.id || r.path}-${i}`}
                onClick={() => handleSelect(r)}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md hover:bg-accent text-left transition-colors">
                <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{r.title}</p>
                  {r.subtitle && <p className="truncate text-xs text-muted-foreground">{r.subtitle}</p>}
                </div>
                <span className="text-[10px] text-muted-foreground capitalize shrink-0">{r.type}</span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
